import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApi } from "../lib/apiClient";
import type { SyncAction, SyncRun } from "../lib/types";
import { emailLabel, RunStatus } from "../components/runs";
import { ExcludeButton, useExclusions } from "../components/exclusions";
import { useAppAuth } from "../contexts/AuthContext";
import { fmtSgtDateTime } from "../lib/datetime";

export default function RunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { request } = useApi();
  const { appUser } = useAppAuth();
  const isAdmin = appUser?.role === "admin";
  const ex = useExclusions(isAdmin);
  const [data, setData] = useState<{ run: SyncRun; actions: SyncAction[] } | null>(
    null,
  );
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    request<{ run: SyncRun; actions: SyncAction[] }>(`/api/sync/runs/${id}`)
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [id, request]);

  if (err) return <div className="page error">{err}</div>;
  if (!data) return <div className="page">Loading…</div>;

  const { run, actions } = data;
  const s = run.summary;
  // The two halves are listed separately: they share a table only by accident
  // of storage, and the per-row allowlist control belongs to locking alone.
  const createActions = actions.filter((a) => a.decision === "create");
  const lockActions = actions.filter((a) => a.decision !== "create");

  return (
    <div className="page">
      <p>
        <Link to="/">← Dashboard</Link>
      </p>
      <h1>
        {run.trigger} run <RunStatus run={run} />
      </h1>
      <p className="muted">
        {fmtSgtDateTime(run.startedAt)} →{" "}
        {run.finishedAt ? fmtSgtDateTime(run.finishedAt) : "…"}
        <br />
        Employee data synced: {fmtSgtDateTime(run.sourceSyncedAt)} (daily mirror)
        <br />
        Audit email: {emailLabel(run)}
        {run.emailedAt && run.emailStatus !== "unrecorded"
          ? ` · ${fmtSgtDateTime(run.emailedAt)}`
          : ""}
        {run.emailAttempts && run.emailAttempts > 1 ? ` · ${run.emailAttempts} attempts` : ""}
      </p>
      {run.emailStatus === "failed" && (
        <p className="error">The audit email for this run was not delivered: {run.emailError}</p>
      )}
      {run.error && <p className="error">Error: {run.error}</p>}
      {s?.threshold.tripped && (
        <p className="error">
          Threshold tripped: {(s.threshold.ratio * 100).toFixed(1)}% &gt;{" "}
          {s.threshold.percent}% — nothing was locked.
        </p>
      )}

      {s && (
        <p>
          {run.dryRun ? "Would lock" : "Locked"}: <strong>{run.dryRun ? s.wouldLock : s.locked}</strong>{" "}
          · matched {s.matched}/{s.totalClerkUsers} · kept {s.activeKept} ·
          already-locked {s.alreadyLocked} · excluded {s.excludedSkipped} ·
          ambiguous {s.ambiguous} · errors {s.errors}
        </p>
      )}

      {!!s?.lockedButEmployed?.length && (
        <>
          <h2>Locked in Clerk but employed in HR ({s.lockedButEmployed.length})</h2>
          <p className="muted">
            The sync never unlocks. If these people should have access — a
            rehire, or a lock made by mistake — unlock them in Clerk.
          </p>
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Employee</th>
                <th>HR</th>
              </tr>
            </thead>
            <tbody>
              {s.lockedButEmployed.map((l) => (
                <tr key={l.email}>
                  <td>{l.email}</td>
                  <td>{l.employeeNo ?? "—"}</td>
                  <td>
                    {l.employmentStatus}
                    {l.lastWorkingDay ? `, last day ${l.lastWorkingDay}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {!!s?.exemptButLeft?.length && (
        <>
          <h2>Exempt though HR says they have left ({s.exemptButLeft.length})</h2>
          <p className="muted">
            An exemption is keeping these logins open in every app. Remove it,
            or give it an end date if the access is meant to continue a while.
          </p>
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>HR</th>
                <th>Exempt through</th>
              </tr>
            </thead>
            <tbody>
              {s.exemptButLeft.map((e) => (
                <tr key={e.email}>
                  <td>{e.email}</td>
                  <td>
                    {e.employmentStatus ?? "—"}
                    {e.lastWorkingDay ? `, last day ${e.lastWorkingDay}` : ""}
                  </td>
                  <td>
                    {e.source === "admin"
                      ? "app admin (always exempt)"
                      : e.source === "env"
                        ? "SYNC_EXCLUDE_EMAILS"
                        : `allowlist${e.expiresAt ? `, until ${e.expiresAt}` : ", no end date"}${e.addedBy ? `, added by ${e.addedBy}` : ""}${e.note ? ` — ${e.note}` : ""}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {s?.provision && s.provision.mode !== "off" && (
        <p>
          Accounts:{" "}
          <strong>
            {s.provision.mode === "enforce"
              ? s.provision.created
              : s.provision.wouldCreate}
          </strong>{" "}
          {s.provision.mode === "enforce" ? "created" : "would be created"} of{" "}
          {s.provision.eligible} eligible
          {s.provision.deferredByCap > 0 &&
            ` · ${s.provision.deferredByCap} deferred to later runs (cap ${s.provision.cap}/run)`}
          {s.provision.createErrors > 0 &&
            ` · ${s.provision.createErrors} errors`}
        </p>
      )}

      <h2>Locks ({lockActions.length})</h2>
      {isAdmin && (
        <p className="muted">
          <strong>Never lock</strong> adds the email to the allowlist so future
          runs skip it. It does not unlock anyone already locked — that is a
          manual re-grant. Manage the list in{" "}
          <Link to="/settings">Settings</Link>.
        </p>
      )}
      {ex.error && <p className="error">{ex.error}</p>}
      {lockActions.length === 0 ? (
        <p className="muted">No notable actions (all kept active).</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Outcome</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Last day</th>
              <th>Via</th>
              <th>Sessions</th>
              {isAdmin && <th>Allowlist</th>}
            </tr>
          </thead>
          <tbody>
            {lockActions.map((a) => (
              <tr key={a.id}>
                <td>{a.canonicalEmail ?? a.email ?? "—"}</td>
                <td>{a.outcome}</td>
                <td>
                  {a.reason ?? "—"}
                  {a.viaEmployeeNo && (
                    <span className="muted"> (via employee #{a.viaEmployeeNo})</span>
                  )}
                </td>
                <td>{a.employmentStatus ?? "—"}</td>
                <td>{a.lastWorkingDay ?? "—"}</td>
                <td>{a.mechanism ?? "—"}</td>
                <td>{a.sessionsRevoked}</td>
                {isAdmin && (
                  <td className="actions">
                    <ExcludeButton
                      email={a.canonicalEmail ?? a.email}
                      ex={ex}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {createActions.length > 0 && (
        <>
          <h2>Accounts ({createActions.length})</h2>
          <p className="muted">
            Staff-level employees who had no Clerk account. Creating one grants
            an identity for sign-in only — what each app lets them do is still
            that app's own decision.
          </p>
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Outcome</th>
                <th>Employee</th>
                <th>Status</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {createActions.map((a) => (
                <tr key={a.id}>
                  <td>{a.email ?? a.canonicalEmail ?? "—"}</td>
                  <td>{a.outcome}</td>
                  <td>{a.viaEmployeeNo ?? "—"}</td>
                  <td>{a.employmentStatus ?? "—"}</td>
                  <td>{a.error ?? a.reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
